import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Mail, KeyRound } from "lucide-react";
import { SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/login")({
  ssr: false,
  validateSearch: (s: Record<string, unknown>) => ({
    next: typeof s.next === "string" && /^\/(?![\/\\])[^\\\s]*$/.test(s.next)
      ? s.next
      : undefined,
  }),
  component: LoginPage,
  head: () => ({
    meta: [
      { title: "Inloggen met code — Fedor Timmerwerken" },
      { name: "description", content: "Log in met een eenmalige code per e-mail." },
      { property: "og:title", content: "Inloggen met code — Fedor Timmerwerken" },
      { property: "og:description", content: "Log in met een eenmalige code per e-mail." },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/login` }],
  }),
});

const ALLOWED_EMAILS = ["yamani.mondt@gmail.com", "info@fedortimmerwerken.nl"];

function LoginPage() {
  const navigate = useNavigate();
  const { next } = Route.useSearch();
  const goNext = () => {
    if (next) {
      try {
        const url = new URL(next, window.location.origin);
        if (url.origin === window.location.origin) {
          window.location.href = url.pathname + url.search + url.hash;
          return;
        }
      } catch {
        // fall through to admin
      }
    }
    navigate({ to: "/admin", replace: true });
  };
  const [email, setEmail] = useState(ALLOWED_EMAILS[0]);
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"request" | "verify">("request");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) goNext();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, next]);

  const sendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (!ALLOWED_EMAILS.includes(email.trim().toLowerCase())) {
        throw new Error("Dit e-mailadres is niet gemachtigd om in te loggen.");
      }
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim().toLowerCase(),
        options: { shouldCreateUser: true, emailRedirectTo: `${window.location.origin}${next ?? "/admin"}` },
      });
      if (error) throw error;
      setStep("verify");
      setInfo("We hebben een code naar je e-mail gestuurd. Voer deze hieronder in.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Onbekende fout");
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { error } = await supabase.auth.verifyOtp({
        email: email.trim().toLowerCase(),
        token: code.trim(),
        type: "email",
      });
      if (error) throw error;
      goNext();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Onbekende fout");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-16">
      <h1 className="font-heading text-3xl font-semibold text-foreground">Inloggen met code</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        We sturen een eenmalige code naar je e-mailadres.
      </p>

      {step === "request" ? (
        <form onSubmit={sendCode} className="mt-8 space-y-4">
          <div>
            <label htmlFor="email" className="text-sm font-medium">
              E-mailadres
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-background px-4 py-3 text-base"
            />
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-70"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Stuur code
          </button>
        </form>
      ) : (
        <form onSubmit={verifyCode} className="mt-8 space-y-4">
          <p className="text-sm text-muted-foreground">
            Code verstuurd naar <strong>{email}</strong>
          </p>
          <div>
            <label htmlFor="code" className="text-sm font-medium">
              Eenmalige code
            </label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-background px-4 py-3 text-center text-lg tracking-widest"
              placeholder="123456"
            />
          </div>
          {info && <p className="text-sm text-primary">{info}</p>}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-70"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <KeyRound className="h-4 w-4" />
            )}
            Verifieer & login
          </button>
          <button
            type="button"
            onClick={() => {
              setStep("request");
              setCode("");
              setError(null);
              setInfo(null);
            }}
            className="w-full text-center text-xs text-muted-foreground underline-offset-4 hover:underline"
          >
            Andere code aanvragen
          </button>
        </form>
      )}

      <p className="pt-6 text-center text-xs text-muted-foreground">
        <Link to="/" className="hover:underline">
          ← Terug naar de site
        </Link>
      </p>
    </div>
  );
}
